import { useMemo, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mindestens, type Rolle } from "@cubic/shared";
import { Button } from "@/components/Button";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Label } from "@/components/Label";
import { Textarea } from "@/components/Textarea";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Pruefquadrat } from "@/components/entwurf/Pruefquadrat";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { api } from "@/lib/api";
import { ALLE_PROJEKTE, useProjektAuswahl } from "@/lib/projekt-auswahl";
import { waehleProjektkontext } from "@/lib/projektkontext";
import { fehlerMeldung } from "@/lib/haken";
import { cx, focusRing } from "@/lib/utils";

type ProjektplanListe = Awaited<
  ReturnType<typeof api.metarr.projektplaene.query>
>;
type Projektplan = ProjektplanListe[number];
type ProjektplanDetail = Awaited<
  ReturnType<typeof api.metarr.projektplan.query>
>;
type ProjektplanAktion = ProjektplanDetail["aktionen"][number];
type MetarrEntwurf = Awaited<
  ReturnType<typeof api.metarr.entwuerfe.query>
>[number];
type AuswahlAktion = {
  id: string;
  zustand: string;
  voraussetzungIds: readonly string[];
};

type Namensverzeichnis = ReadonlyMap<string, string>;

const OFFENE_PLANZUSTAENDE = new Set(["offen", "teilweise"]);
const AKTIVE_LAUFZUSTAENDE = new Set(["wartet", "schreibt", "laeuft"]);

const AKTIONSNAMEN: Record<string, string> = {
  meilenstein_anlegen: "Meilenstein anlegen",
  meilenstein_aendern: "Meilenstein ändern",
  meilenstein_archivieren: "Meilenstein archivieren",
  aufgabe_anlegen: "Aufgabe anlegen",
  aufgabe_aendern: "Aufgabe ändern",
  aufgabe_meilenstein_setzen: "Aufgabe mit Meilenstein verknüpfen",
  aufgabe_status_setzen: "Aufgabenstatus setzen",
  aufgabe_zuweisen: "Aufgabe zuweisen",
};

const PLANZUSTAENDE: Record<string, string> = {
  offen: "Bereit zur Prüfung",
  teilweise: "Teilweise entschieden",
  ausgefuehrt: "Ausgeführt",
  abgelehnt: "Abgelehnt",
  gescheitert: "Gescheitert",
};

const AKTIONSZUSTAENDE: Record<string, string> = {
  offen: "Offen",
  ausgefuehrt: "Ausgeführt",
  abgelehnt: "Abgelehnt",
  blockiert: "Blockiert",
};

const LAUFZUSTAENDE: Record<string, string> = {
  wartet: "Wartet auf Verarbeitung",
  schreibt: "Metarr bereitet den Plan vor",
  laeuft: "Metarr bereitet den Plan vor",
  rueckfrage: "Rückfrage wartet",
  vorschlag: "Plan liegt zur Prüfung vor",
  fertig: "Vorbereitung abgeschlossen",
  gescheitert: "Vorbereitung gescheitert",
};

function vorZeit(wert: string | Date | null | undefined): string {
  if (!wert) return "";
  const minuten = Math.max(
    0,
    Math.floor((Date.now() - new Date(wert).getTime()) / 60_000),
  );
  if (minuten < 1) return "gerade eben";
  if (minuten < 60) return `vor ${minuten} Min.`;
  const stunden = Math.floor(minuten / 60);
  if (stunden < 24) return `vor ${stunden} Std.`;
  return `vor ${Math.floor(stunden / 24)} Tagen`;
}

function kurz(wert: unknown, grenze = 180): string {
  if (typeof wert !== "string") return "";
  const einzeilig = wert.replace(/\s+/g, " ").trim();
  return einzeilig.length > grenze
    ? `${einzeilig.slice(0, grenze - 1)}…`
    : einzeilig;
}

/** Die unveränderte, vollständige Prüfwahrheit aus dem Plan-Service. */
export function formatiereProjektplanArgumente(argumente: unknown): string {
  return JSON.stringify(argumente, null, 2);
}

/**
 * Ob im geführten Projekt geplant werden darf.
 *
 * Das Projekt kommt aus der Schiene — dieselbe Wahl, die oben links steht.
 * Planen kann man dort aber nur mit Schreibrecht in einem aktiven Projekt.
 * Auf ein anderes auszuweichen wäre falsch: Dann stünde oben links das eine
 * und der Plan entstünde für das andere.
 */
export function planbaresProjekt<T extends { id: string }>(
  gefuehrt: T | undefined,
  planbareIds: ReadonlySet<string>,
): T | undefined {
  if (!gefuehrt) return undefined;
  return planbareIds.has(gefuehrt.id) ? gefuehrt : undefined;
}

export function istSchreibbareProjektrolle(
  rolle: Rolle | undefined,
): boolean {
  return rolle !== undefined && mindestens(rolle, "mitglied");
}

function objekt(wert: unknown): Record<string, unknown> | null {
  return typeof wert === "object" && wert !== null && !Array.isArray(wert)
    ? (wert as Record<string, unknown>)
    : null;
}

function referenz(
  wert: unknown,
  namen: Namensverzeichnis = new Map(),
): string {
  const eintrag = objekt(wert);
  if (!eintrag) return "";
  if (typeof eintrag["ausAktion"] === "string") {
    const lokaleKennung = eintrag["ausAktion"];
    const name = namen.get(`aktion:${lokaleKennung}`);
    return name
      ? `Ergebnis aus ${lokaleKennung} (${name})`
      : `Ergebnis aus ${lokaleKennung}`;
  }
  if (typeof eintrag["id"] === "string") {
    const id = eintrag["id"];
    const name = namen.get(id);
    return name ? `${name} (${id})` : `vorhandenes Objekt ${id}`;
  }
  return "";
}

function aktionsTitel(aktion: ProjektplanAktion): string {
  const argumente = objekt(aktion.argumente) ?? {};
  const name = kurz(argumente["name"]);
  const titel = kurz(argumente["titel"]);
  const zusatz = name || titel;
  const art = AKTIONSNAMEN[aktion.art] ?? aktion.art;
  return zusatz ? `${art}: ${zusatz}` : art;
}

function argumentZusammenfassung(
  aktion: ProjektplanAktion,
  namen: Namensverzeichnis,
): string {
  const a = objekt(aktion.argumente) ?? {};
  const teile: string[] = [];
  const inhalt = kurz(a["inhalt"] ?? a["beschreibung"]);
  if (inhalt) teile.push(`Text: ${inhalt}`);
  if (a["faelligAm"] !== undefined && a["faelligAm"] !== null) {
    teile.push(`Fällig: ${String(a["faelligAm"])}`);
  }
  if (a["prioritaet"] !== undefined) {
    teile.push(`Priorität: ${String(a["prioritaet"])}`);
  }
  if (a["aufgabenart"] !== undefined) {
    teile.push(`Art: ${String(a["aufgabenart"])}`);
  }
  if (a["schaetzungMinuten"] !== undefined) {
    teile.push(
      a["schaetzungMinuten"] === null
        ? "Schätzung entfernen"
        : `Schätzung: ${String(a["schaetzungMinuten"])} Min.`,
    );
  }
  const aufgabe = referenz(a["aufgabe"], namen);
  if (aufgabe) teile.push(`Aufgabe: ${aufgabe}`);
  const meilenstein = referenz(a["meilenstein"], namen);
  if (a["meilenstein"] === null) teile.push("Meilenstein entfernen");
  else if (meilenstein) teile.push(`Meilenstein: ${meilenstein}`);
  const elternAufgabe = referenz(a["elternAufgabe"], namen);
  if (elternAufgabe) teile.push(`Unteraufgabe von: ${elternAufgabe}`);
  if (Array.isArray(a["personIds"])) {
    const zuweisungen = a["personIds"].map((id) =>
      typeof id === "string" ? namen.get(id) ?? id : String(id),
    );
    teile.push(`Zuweisungen: ${zuweisungen.join(", ") || "keine"}`);
  }
  if (typeof a["zustandId"] === "string") {
    teile.push(
      `Neuer Aufgabenstatus: ${namen.get(a["zustandId"]) ?? a["zustandId"]}`,
    );
  }
  if (typeof a["erwarteteVersion"] === "number") {
    teile.push(`geprüfte Version ${a["erwarteteVersion"]}`);
  }
  if (teile.length === 0) {
    const name = kurz(a["name"] ?? a["titel"]);
    return name || "Keine weiteren Argumente.";
  }
  return teile.join(" · ");
}

function sammleAufloesungen(
  wert: unknown,
  namen: Namensverzeichnis,
  pfad = "argumente",
): Array<{ pfad: string; id: string; name: string }> {
  if (typeof wert === "string") {
    const name = namen.get(wert);
    return name ? [{ pfad, id: wert, name }] : [];
  }
  if (Array.isArray(wert)) {
    return wert.flatMap((eintrag, index) =>
      sammleAufloesungen(eintrag, namen, `${pfad}[${index}]`),
    );
  }
  const eintrag = objekt(wert);
  if (!eintrag) return [];
  return Object.entries(eintrag).flatMap(([schluessel, inhalt]) => {
    const aktuellerPfad = `${pfad}.${schluessel}`;
    if (schluessel === "ausAktion" && typeof inhalt === "string") {
      const name = namen.get(`aktion:${inhalt}`);
      return name
        ? [{ pfad: aktuellerPfad, id: inhalt, name }]
        : [];
    }
    if (typeof inhalt === "string") {
      const name = namen.get(inhalt);
      return name
        ? [{ pfad: aktuellerPfad, id: inhalt, name }]
        : [];
    }
    return sammleAufloesungen(inhalt, namen, aktuellerPfad);
  });
}

function quellenVon(aktion: ProjektplanAktion) {
  if (!Array.isArray(aktion.quellen)) return [];
  return aktion.quellen.flatMap((wert) => {
    const quelle = objekt(wert);
    if (!quelle || typeof quelle["beleg"] !== "string") return [];
    return [
      {
        art: typeof quelle["art"] === "string" ? quelle["art"] : "Quelle",
        id: typeof quelle["id"] === "string" ? quelle["id"] : "",
        beleg: quelle["beleg"],
      },
    ];
  });
}

async function invalidierePlanDaten(
  client: ReturnType<typeof useQueryClient>,
  planId: string,
  projektId: string,
) {
  await Promise.all([
    client.invalidateQueries({
      queryKey: ["cubic", "metarr", "projektplaene"],
    }),
    client.invalidateQueries({
      queryKey: ["cubic", "metarr", "projektplan", planId],
    }),
    client.invalidateQueries({ queryKey: ["cubic", "metarr", "entwuerfe"] }),
    client.invalidateQueries({ queryKey: ["cubic", "navigation"] }),
    client.invalidateQueries({ queryKey: ["cubic", "projekte"] }),
    client.invalidateQueries({ queryKey: ["cubic", "projekt", projektId] }),
    client.invalidateQueries({
      queryKey: ["cubic", "aufgaben", projektId],
    }),
    client.invalidateQueries({ queryKey: ["cubic", "aufgaben", "alle"] }),
    client.invalidateQueries({
      queryKey: ["cubic", "meilensteine", projektId],
    }),
  ]);
}

function istWaehbar(
  id: string,
  nachId: ReadonlyMap<string, AuswahlAktion>,
  besucht = new Set<string>(),
): boolean {
  const aktion = nachId.get(id);
  if (!aktion) return false;
  if (aktion.zustand === "ausgefuehrt") return true;
  if (aktion.zustand !== "offen" || besucht.has(id)) return false;
  const naechsteBesucht = new Set(besucht).add(id);
  return aktion.voraussetzungIds.every((voraussetzungId) =>
    istWaehbar(voraussetzungId, nachId, naechsteBesucht),
  );
}

/**
 * Freigaben bleiben transitiv abhängigkeitsgeschlossen. Beim Abwählen einer
 * Voraussetzung verschwinden nur die davon abhängigen Freigaben.
 */
export function aendereFreigabeAuswahl(
  aktionen: readonly AuswahlAktion[],
  aktuell: ReadonlySet<string>,
  aktionId: string,
): Set<string> {
  const nachId = new Map(aktionen.map((aktion) => [aktion.id, aktion]));
  const naechste = new Set(
    [...aktuell].filter((id) => nachId.get(id)?.zustand === "offen"),
  );
  if (naechste.has(aktionId)) {
    const entfernt = new Set([aktionId]);
    let hatSichGeaendert = true;
    while (hatSichGeaendert) {
      hatSichGeaendert = false;
      for (const id of naechste) {
        const aktion = nachId.get(id);
        if (
          !entfernt.has(id) &&
          aktion?.voraussetzungIds.some((voraussetzungId) =>
            entfernt.has(voraussetzungId),
          )
        ) {
          entfernt.add(id);
          hatSichGeaendert = true;
        }
      }
    }
    for (const id of entfernt) naechste.delete(id);
    return naechste;
  }

  if (!istWaehbar(aktionId, nachId)) return naechste;
  const fuegeHinzu = (id: string) => {
    const aktion = nachId.get(id);
    if (!aktion || aktion.zustand === "ausgefuehrt" || naechste.has(id)) {
      return;
    }
    for (const voraussetzungId of aktion.voraussetzungIds) {
      fuegeHinzu(voraussetzungId);
    }
    if (aktion.zustand === "offen") naechste.add(id);
  };
  fuegeHinzu(aktionId);
  return naechste;
}

/** Ablehnung ist absichtlich exakt und zieht keine Voraussetzung mit. */
export function aendereAblehnAuswahl(
  aktuell: ReadonlySet<string>,
  aktionId: string,
): Set<string> {
  const naechste = new Set(aktuell);
  if (naechste.has(aktionId)) naechste.delete(aktionId);
  else naechste.add(aktionId);
  return naechste;
}

function ProjektplanDetails({
  plan,
  kannSchreiben,
}: {
  plan: Projektplan;
  kannSchreiben: boolean;
}) {
  const client = useQueryClient();
  const [freigabeAuswahl, setzeFreigabeAuswahl] = useState<Set<string>>(
    () => new Set(),
  );
  const [ablehnAuswahl, setzeAblehnAuswahl] = useState<Set<string>>(
    () => new Set(),
  );
  const [pruefdatenOffen, setzePruefdatenOffen] = useState<Set<string>>(
    () => new Set(),
  );
  const [freigabeId, setzeFreigabeId] = useState(() => crypto.randomUUID());
  const detail = useQuery({
    queryKey: ["cubic", "metarr", "projektplan", plan.id],
    queryFn: () => api.metarr.projektplan.query({ planId: plan.id }),
  });
  const aktionen = detail.data?.aktionen ?? [];
  const verzeichnis = useQuery({
    queryKey: ["cubic", "projekt", plan.projektId, "verzeichnis"],
    queryFn: () =>
      api.projekte.verzeichnis.query({ projektId: plan.projektId }),
  });
  const projektAufgaben = useQuery({
    queryKey: [
      "cubic",
      "aufgaben",
      plan.projektId,
      "projektplan-pruefung",
      { archivierte: true },
    ],
    queryFn: () =>
      api.aufgaben.liste.query({
        projektId: plan.projektId,
        archivierte: true,
      }),
  });
  const nachId = useMemo(
    () => new Map(aktionen.map((aktion) => [aktion.id, aktion])),
    [aktionen],
  );
  const namen = useMemo(() => {
    const ergebnis = new Map<string, string>();
    const ordner = verzeichnis.data;
    if (ordner) {
      ergebnis.set(
        ordner.projekt.id,
        `${ordner.projekt.kuerzel} — ${ordner.projekt.name}`,
      );
      for (const person of ordner.personen) ergebnis.set(person.id, person.name);
      for (const zustand of ordner.zustaende) {
        ergebnis.set(zustand.id, zustand.name);
      }
      for (const label of ordner.labels) ergebnis.set(label.id, label.name);
      for (const meilenstein of ordner.meilensteine) {
        ergebnis.set(
          meilenstein.id,
          `Meilenstein ${meilenstein.nummer}: ${meilenstein.name}`,
        );
      }
    }
    const kuerzel = ordner?.projekt.kuerzel ?? "Aufgabe";
    for (const aufgabe of projektAufgaben.data ?? []) {
      ergebnis.set(
        aufgabe.id,
        `${kuerzel}-${aufgabe.nummer}: ${aufgabe.titel}`,
      );
    }
    for (const aktion of aktionen) {
      ergebnis.set(`aktion:${aktion.lokaleKennung}`, aktionsTitel(aktion));
    }
    return ergebnis;
  }, [aktionen, projektAufgaben.data, verzeichnis.data]);
  const wirksameFreigabeAuswahl = useMemo(
    () =>
      new Set(
        [...freigabeAuswahl].filter(
          (id) => nachId.get(id)?.zustand === "offen",
        ),
      ),
    [freigabeAuswahl, nachId],
  );
  const wirksameAblehnAuswahl = useMemo(
    () =>
      new Set(
        [...ablehnAuswahl].filter(
          (id) => nachId.get(id)?.zustand === "offen",
        ),
      ),
    [ablehnAuswahl, nachId],
  );

  function aendereFreigabe(aktionId: string) {
    const naechste = aendereFreigabeAuswahl(
      aktionen,
      wirksameFreigabeAuswahl,
      aktionId,
    );
    setzeFreigabeAuswahl(naechste);
    if (naechste.has(aktionId)) {
      setzeAblehnAuswahl((aktuell) => {
        const bereinigt = new Set(aktuell);
        for (const id of naechste) bereinigt.delete(id);
        return bereinigt;
      });
    }
    setzeFreigabeId(crypto.randomUUID());
  }

  function aendereAblehnung(aktionId: string) {
    const wirdAbgelehnt = !wirksameAblehnAuswahl.has(aktionId);
    setzeAblehnAuswahl(
      aendereAblehnAuswahl(wirksameAblehnAuswahl, aktionId),
    );
    if (wirdAbgelehnt && wirksameFreigabeAuswahl.has(aktionId)) {
      setzeFreigabeAuswahl(
        aendereFreigabeAuswahl(
          aktionen,
          wirksameFreigabeAuswahl,
          aktionId,
        ),
      );
      setzeFreigabeId(crypto.randomUUID());
    }
  }

  const bestaetigen = useMutation({
    mutationFn: (eingabe: {
      planId: string;
      freigabeId: string;
      aktionIds: string[];
    }) => api.metarr.projektplanBestaetigen.mutate(eingabe),
    onSuccess: async () => {
      setzeFreigabeAuswahl(new Set());
      setzeAblehnAuswahl(new Set());
      setzeFreigabeId(crypto.randomUUID());
      await invalidierePlanDaten(client, plan.id, plan.projektId);
    },
  });
  const ablehnen = useMutation({
    mutationFn: (aktionIds: string[]) =>
      api.metarr.projektplanAblehnen.mutate({
        planId: plan.id,
        aktionIds,
      }),
    onSuccess: async () => {
      setzeFreigabeAuswahl(new Set());
      setzeAblehnAuswahl(new Set());
      setzeFreigabeId(crypto.randomUUID());
      await invalidierePlanDaten(client, plan.id, plan.projektId);
    },
  });

  if (detail.isPending) {
    return (
      <p className="mt-4 text-[13px] text-tinte3" role="status">
        Plan wird geladen …
      </p>
    );
  }
  if (detail.error) {
    return (
      <div className="mt-4">
        <Fehlerzeile
          meldung={fehlerMeldung(detail.error)}
          onNeuLaden={() => void detail.refetch()}
          onErneut={() => void detail.refetch()}
        />
      </div>
    );
  }

  const mutationFehler = bestaetigen.error ?? ablehnen.error;
  const istPending = bestaetigen.isPending || ablehnen.isPending;

  return (
    <div className="mt-4 space-y-4">
      <p className="max-w-[70ch] text-[13.5px] font-light leading-[1.6] text-tinte3">
        {detail.data?.plan.zusammenfassung}
      </p>
      <p className="font-mono text-[11px] leading-[1.6] text-tinte4">
        Freigabe und Ablehnung sind getrennte Entscheidungen. Eine Freigabe
        nimmt offene Voraussetzungen automatisch mit. Eine Ablehnung betrifft
        ausschließlich die markierten Aktionen; abhängige Aktionen werden vom
        Server blockiert, ihre Voraussetzungen aber nicht abgelehnt.
      </p>
      {!kannSchreiben ? (
        <p className="text-[12px] text-akzent" role="status">
          Für dieses Projekt ist keine aktuelle Schreibrolle erkennbar. Der Plan
          bleibt lesbar, kann hier aber nicht entschieden werden.
        </p>
      ) : null}
      {verzeichnis.error || projektAufgaben.error ? (
        <p className="text-[12px] text-akzent" role="status">
          Namen konnten nicht vollständig aufgelöst werden. Die vollständigen
          IDs und unveränderten Argumente bleiben in den Prüfdaten sichtbar.
        </p>
      ) : null}

      <div className="space-y-0">
        {aktionen.map((aktion) => {
          const offen = aktion.zustand === "offen";
          const waehbar = offen && istWaehbar(aktion.id, nachId);
          const quellen = quellenVon(aktion);
          const aufloesungen = sammleAufloesungen(aktion.argumente, namen);
          const vollstaendigOffen = pruefdatenOffen.has(aktion.id);
          const voraussetzungen = aktion.voraussetzungIds
            .map((id) => nachId.get(id))
            .filter((eintrag): eintrag is ProjektplanAktion => Boolean(eintrag));
          return (
            <div
              key={aktion.id}
              className="border-b border-linie py-4 first:border-t"
            >
              <div className="flex items-start gap-3">
                {offen ? (
                  <div className="mt-1 flex w-[92px] flex-none flex-col gap-2">
                    <fieldset
                      disabled={!kannSchreiben || !waehbar || istPending}
                      className={cx(
                        "flex items-center gap-2",
                        (!kannSchreiben || !waehbar) && "opacity-40",
                      )}
                    >
                      <Pruefquadrat
                        erfuellt={wirksameFreigabeAuswahl.has(aktion.id)}
                        onKlick={() => aendereFreigabe(aktion.id)}
                        ariaLabel={`${aktionsTitel(aktion)} zur Freigabe auswählen`}
                      />
                      <span className="font-mono text-[9.5px] text-tinte4">
                        Freigeben
                      </span>
                    </fieldset>
                    <fieldset
                      disabled={!kannSchreiben || istPending}
                      className={cx(
                        "flex items-center gap-2",
                        !kannSchreiben && "opacity-40",
                      )}
                    >
                      <Pruefquadrat
                        erfuellt={wirksameAblehnAuswahl.has(aktion.id)}
                        onKlick={() => aendereAblehnung(aktion.id)}
                        ariaLabel={`${aktionsTitel(aktion)} zur Ablehnung auswählen`}
                      />
                      <span className="font-mono text-[9.5px] text-tinte4">
                        Ablehnen
                      </span>
                    </fieldset>
                  </div>
                ) : (
                  <span
                    aria-hidden="true"
                    className={cx(
                      "mt-1 size-[15px] flex-none",
                      aktion.zustand === "ausgefuehrt"
                        ? "bg-tinte"
                        : "shadow-[inset_0_0_0_1px_var(--tinte4)] opacity-40",
                    )}
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <p className="text-[14px] leading-[1.5] text-tinte">
                      {aktionsTitel(aktion)}
                    </p>
                    <Mikrolabel
                      grad={10.5}
                      className={cx(
                        aktion.zustand === "blockiert" && "text-akzent",
                      )}
                    >
                      {AKTIONSZUSTAENDE[aktion.zustand] ?? aktion.zustand}
                    </Mikrolabel>
                    <Mikrolabel grad={10.5}>{aktion.lokaleKennung}</Mikrolabel>
                  </div>
                  <p className="mt-1.5 text-[13px] font-light leading-[1.55] text-tinte3">
                    {aktion.begruendung}
                  </p>
                  <p className="mt-1.5 font-mono text-[10.5px] leading-[1.6] text-tinte4">
                    {argumentZusammenfassung(aktion, namen)}
                  </p>
                  {voraussetzungen.length > 0 ? (
                    <p className="mt-1 font-mono text-[10.5px] leading-[1.6] text-tinte4">
                      Benötigt: {voraussetzungen.map(aktionsTitel).join(", ")}
                    </p>
                  ) : null}
                  {quellen.length > 0 ? (
                    <div className="mt-2 space-y-1">
                      {quellen.map((quelle, index) => (
                        <p
                          key={`${quelle.id}-${index}`}
                          className="bg-wisch px-3 py-2 font-mono text-[10.5px] leading-[1.55] text-tinte3"
                        >
                          {quelle.art}
                          {quelle.id ? `:${quelle.id}` : ""}
                          {quelle.id && namen.has(quelle.id)
                            ? ` (${namen.get(quelle.id)})`
                            : ""}{" "}
                          — „
                          {quelle.beleg}“
                        </p>
                      ))}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    aria-expanded={vollstaendigOffen}
                    onClick={() =>
                      setzePruefdatenOffen((aktuell) => {
                        const naechste = new Set(aktuell);
                        if (naechste.has(aktion.id)) naechste.delete(aktion.id);
                        else naechste.add(aktion.id);
                        return naechste;
                      })
                    }
                    className={cx(
                      "mt-2 font-mono text-[10.5px] text-akzent hover:underline",
                      focusRing,
                    )}
                  >
                    {vollstaendigOffen
                      ? "Vollständige Prüfdaten schließen"
                      : "Vollständige Prüfdaten anzeigen"}
                  </button>
                  {vollstaendigOffen ? (
                    <div className="mt-3 space-y-3 bg-wisch px-4 py-3">
                      <div className="space-y-1 font-mono text-[10.5px] leading-[1.6] text-tinte3">
                        <p>
                          <span className="text-tinte4">Aktions-ID:</span>{" "}
                          {aktion.id}
                        </p>
                        <p>
                          <span className="text-tinte4">Lokale Kennung:</span>{" "}
                          {aktion.lokaleKennung}
                        </p>
                        <p>
                          <span className="text-tinte4">Aktionsart:</span>{" "}
                          {aktion.art}
                        </p>
                        <p>
                          <span className="text-tinte4">
                            Voraussetzung-IDs:
                          </span>{" "}
                          {aktion.voraussetzungIds.length > 0
                            ? aktion.voraussetzungIds.join(", ")
                            : "keine"}
                        </p>
                      </div>
                      {aufloesungen.length > 0 ? (
                        <div>
                          <Mikrolabel grad={10.5}>Sicher aufgelöste Namen</Mikrolabel>
                          <ul className="mt-1 space-y-1 font-mono text-[10.5px] leading-[1.6] text-tinte3">
                            {aufloesungen.map((eintrag) => (
                              <li key={`${eintrag.pfad}-${eintrag.id}`}>
                                {eintrag.pfad}: {eintrag.name} ({eintrag.id})
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      <div>
                        <Mikrolabel grad={10.5}>
                          Argumente unverändert vom Server
                        </Mikrolabel>
                        <pre
                          className="mt-1 max-h-[460px] overflow-auto whitespace-pre-wrap break-all bg-papier px-3 py-2 font-mono text-[10.5px] leading-[1.6] text-tinte"
                          aria-label={`Unveränderte Argumente für ${aktionsTitel(aktion)}`}
                        >
                          {formatiereProjektplanArgumente(aktion.argumente)}
                        </pre>
                      </div>
                      <div>
                        <Mikrolabel grad={10.5}>
                          Quellen unverändert vom Server
                        </Mikrolabel>
                        <pre
                          className="mt-1 max-h-[320px] overflow-auto whitespace-pre-wrap break-all bg-papier px-3 py-2 font-mono text-[10.5px] leading-[1.6] text-tinte"
                          aria-label={`Unveränderte Quellen für ${aktionsTitel(aktion)}`}
                        >
                          {JSON.stringify(aktion.quellen, null, 2)}
                        </pre>
                      </div>
                    </div>
                  ) : null}
                  {!waehbar && offen ? (
                    <p className="mt-2 text-[11.5px] text-akzent">
                      Eine Voraussetzung ist nicht mehr offen oder ausgeführt.
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {mutationFehler ? (
        <Fehlerzeile
          meldung={fehlerMeldung(mutationFehler)}
          onNeuLaden={() => void detail.refetch()}
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-2.5">
        <Button
          className="h-[31px] px-3.5 font-mono text-[11.5px]"
          disabled={
            !kannSchreiben ||
            wirksameFreigabeAuswahl.size === 0 ||
            istPending
          }
          isLoading={bestaetigen.isPending}
          loadingText="Wird ausgeführt"
          onClick={() =>
            bestaetigen.mutate({
              planId: plan.id,
              freigabeId,
              aktionIds: [...wirksameFreigabeAuswahl],
            })
          }
        >
          Freigabe ausführen
        </Button>
        <Button
          variant="ghost"
          className="h-[31px] px-3.5 font-mono text-[11.5px]"
          disabled={
            !kannSchreiben ||
            wirksameAblehnAuswahl.size === 0 ||
            istPending
          }
          isLoading={ablehnen.isPending}
          loadingText="Wird abgelehnt"
          onClick={() => ablehnen.mutate([...wirksameAblehnAuswahl])}
        >
          Markierte ablehnen
        </Button>
        <Mikrolabel grad={10.5}>
          {wirksameFreigabeAuswahl.size} zur Freigabe ·{" "}
          {wirksameAblehnAuswahl.size} zur Ablehnung ·{" "}
          {aktionen.filter((a) => a.zustand === "offen").length} offen
        </Mikrolabel>
      </div>
    </div>
  );
}

function ProjektplanRueckfrage({
  lauf,
  kannAntworten,
}: {
  lauf: MetarrEntwurf;
  kannAntworten: boolean;
}) {
  const client = useQueryClient();
  const [antwort, setzeAntwort] = useState("");
  const beantworten = useMutation({
    mutationFn: (wert: string) =>
      api.metarr.rueckfrageBeantworten.mutate({
        entwurfId: lauf.id,
        antwort: wert,
      }),
    onSuccess: async () => {
      setzeAntwort("");
      await Promise.all([
        client.invalidateQueries({
          queryKey: ["cubic", "metarr", "entwuerfe"],
        }),
        client.invalidateQueries({
          queryKey: ["cubic", "metarr", "projektplaene"],
        }),
        client.invalidateQueries({ queryKey: ["cubic", "navigation"] }),
      ]);
    },
  });
  const abbrechen = useMutation({
    mutationFn: () =>
      api.metarr.rueckfrageAbbrechen.mutate({ entwurfId: lauf.id }),
    onSuccess: async () => {
      setzeAntwort("");
      await Promise.all([
        client.invalidateQueries({
          queryKey: ["cubic", "metarr", "entwuerfe"],
        }),
        client.invalidateQueries({
          queryKey: ["cubic", "metarr", "projektplaene"],
        }),
        client.invalidateQueries({ queryKey: ["cubic", "navigation"] }),
      ]);
    },
  });
  const feldId = `metarr-plan-rueckfrage-${lauf.id}`;

  return (
    <div className="mt-3 space-y-3 bg-wisch px-4 py-3">
      <div>
        <Mikrolabel grad={10.5} className="text-akzent">
          Rückfrage von Metarr
        </Mikrolabel>
        <p className="mt-1 text-[13px] leading-[1.55] text-tinte">
          {lauf.rueckfrage}
        </p>
        {lauf.rueckfrageEntscheidung ? (
          <p className="mt-1 text-[12px] font-light leading-[1.55] text-tinte3">
            Davon hängt ab: {lauf.rueckfrageEntscheidung}
          </p>
        ) : null}
      </div>
      {kannAntworten ? (
        <>
          <div className="space-y-1.5">
            <Label htmlFor={feldId}>Antwort</Label>
            <Textarea
              id={feldId}
              rows={3}
              maxLength={4_000}
              value={antwort}
              onChange={(ereignis) => {
                setzeAntwort(ereignis.target.value);
                beantworten.reset();
              }}
              disabled={beantworten.isPending}
            />
          </div>
          {beantworten.error ? (
            <Fehlerzeile
              meldung={fehlerMeldung(beantworten.error)}
              onErneut={() => {
                if (antwort.trim()) beantworten.mutate(antwort.trim());
              }}
            />
          ) : null}
          {/*
            Zwei Ausgänge. Ohne den zweiten blieb eine Rückfrage, die niemand
            beantworten wollte, für immer im Zähler stehen.
          */}
          <div className="flex flex-wrap items-center gap-2.5">
            <Button
              type="button"
              className="h-[31px] px-3.5 font-mono text-[11.5px]"
              disabled={
                beantworten.isPending || abbrechen.isPending || !antwort.trim()
              }
              isLoading={beantworten.isPending}
              loadingText="Antwort wird gesendet"
              onClick={() => beantworten.mutate(antwort.trim())}
            >
              Antwort senden
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="h-[31px] px-3.5 font-mono text-[11.5px]"
              disabled={beantworten.isPending || abbrechen.isPending}
              isLoading={abbrechen.isPending}
              loadingText="Wird verworfen"
              onClick={() => abbrechen.mutate()}
            >
              Rückfrage verwerfen
            </Button>
          </div>
          {abbrechen.error ? (
            <Fehlerzeile meldung={fehlerMeldung(abbrechen.error)} />
          ) : null}
          <p className="text-[11.5px] leading-[1.55] text-tinte3">
            Verworfen beendet diesen Lauf. Frage und Verlauf bleiben erhalten.
          </p>
        </>
      ) : (
        <p className="text-[12px] text-akzent" role="status">
          Zum Antworten fehlt die aktuelle Schreibrolle im Projekt.
        </p>
      )}
    </div>
  );
}

export function ProjektplanAbschnitt() {
  const client = useQueryClient();
  /*
   * Das Projekt kommt aus der Schiene, nicht aus einer zweiten Auswahl hier.
   * Zwei Wähler für dieselbe Frage lassen sich auseinanderdrehen — dann steht
   * oben links ein Projekt und der Plan entsteht für ein anderes.
   */
  const { auswahl } = useProjektAuswahl();
  const pfad = useRouterState({ select: (s) => s.location.pathname });
  const [anliegen, setzeAnliegen] = useState("");
  const [aufgeklappt, setzeAufgeklappt] = useState<string | null>(null);

  const ich = useQuery({
    queryKey: ["cubic", "ich"],
    queryFn: () => api.ich.query(),
  });
  const projekte = useQuery({
    queryKey: ["cubic", "projekte", { archivierte: false }],
    queryFn: () => api.projekte.liste.query(),
  });
  const plaene = useQuery({
    queryKey: ["cubic", "metarr", "projektplaene"],
    queryFn: () => api.metarr.projektplaene.query(),
    refetchInterval: 2_000,
  });
  const entwuerfe = useQuery({
    queryKey: ["cubic", "metarr", "entwuerfe"],
    queryFn: () => api.metarr.entwuerfe.query(),
    refetchInterval: (abfrage) =>
      abfrage.state.data?.some(
        (entwurf) =>
          entwurf.arbeitsablauf === "projektplanung" &&
          AKTIVE_LAUFZUSTAENDE.has(entwurf.zustand),
      )
        ? 1_000
        : false,
  });

  const rollenZu = new Map(
    (ich.data?.projekte ?? []).map((projekt) => [
      projekt.projektId,
      projekt.rolle,
    ]),
  );
  const schreibbareProjektIds = new Set(
    [...rollenZu]
      .filter(([, rolle]) => istSchreibbareProjektrolle(rolle))
      .map(([id]) => id),
  );
  const aktiveProjekte = (projekte.data ?? []).filter(
    (projekt) =>
      projekt.archiviertAm === null && schreibbareProjektIds.has(projekt.id),
  );

  /*
   * Dasselbe Verfahren wie in der Schiene, damit beide dasselbe Projekt
   * führen. Gesucht wird bewusst unter allen sichtbaren Projekten, nicht nur
   * unter den schreibbaren: Fiele die Wahl hier still auf ein anderes, stünde
   * oben links das eine und der Plan entstünde für das andere.
   */
  const gefuehrtesProjekt = waehleProjektkontext({
    auswahl,
    alleWert: ALLE_PROJEKTE,
    projekte: projekte.data ?? [],
    pfad,
  });
  const planbar = planbaresProjekt(
    gefuehrtesProjekt,
    new Set(aktiveProjekte.map((projekt) => projekt.id)),
  );
  const darfPlanen = planbar !== undefined;
  const ausgewaehltesProjekt = planbar?.id ?? "";
  const offenePlaene = (plaene.data ?? []).filter((plan) =>
    OFFENE_PLANZUSTAENDE.has(plan.zustand),
  );
  const planIds = new Set((plaene.data ?? []).map((plan) => plan.entwurfId));
  const planLaeufe = (entwuerfe.data ?? []).filter(
    (entwurf) => entwurf.arbeitsablauf === "projektplanung",
  );
  const laufZu = new Map(planLaeufe.map((entwurf) => [entwurf.id, entwurf]));
  const laeufeOhnePlan = planLaeufe.filter(
    (entwurf) =>
      !planIds.has(entwurf.id) &&
      (AKTIVE_LAUFZUSTAENDE.has(entwurf.zustand) ||
        entwurf.zustand === "rueckfrage" ||
        entwurf.zustand === "gescheitert"),
  );

  const vorschlagen = useMutation({
    mutationFn: (eingabe: { projektId: string; satz: string }) =>
      api.metarr.projektplanVorschlagen.mutate(eingabe),
    onSuccess: async () => {
      setzeAnliegen("");
      await Promise.all([
        client.invalidateQueries({
          queryKey: ["cubic", "metarr", "entwuerfe"],
        }),
        client.invalidateQueries({
          queryKey: ["cubic", "metarr", "projektplaene"],
        }),
        client.invalidateQueries({ queryKey: ["cubic", "navigation"] }),
      ]);
    },
  });

  return (
    <section className="mt-13" aria-labelledby="projektplanung-titel">
      <Rubrik anzahl={`${offenePlaene.length} zur Prüfung`}>
        <span id="projektplanung-titel">Projektplanung</span>
      </Rubrik>

      <form
        className="border-b border-linie py-5"
        onSubmit={(ereignis) => {
          ereignis.preventDefault();
          if (!ausgewaehltesProjekt || !anliegen.trim()) return;
          vorschlagen.mutate({
            projektId: ausgewaehltesProjekt,
            satz: anliegen.trim(),
          });
        }}
      >
        <p className="max-w-[68ch] text-[13.5px] font-light leading-[1.6] text-tinte3">
          Metarr liest den sichtbaren Projektkontext und bereitet einen Plan vor.
          Erst eine ausdrückliche Auswahl unten darf Änderungen ausführen.
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-[minmax(190px,0.65fr)_minmax(280px,1.35fr)_auto] md:items-end">
          {/*
            * Anzeige, keine Auswahl: Gewechselt wird oben links in der
            * Schiene. Der Name steht hier trotzdem, denn niemand soll einen
            * Plan für ein Projekt anstoßen, das er nur vermutet.
            */}
          <div className="space-y-1.5">
            <Label>Projekt</Label>
            <p
              className="flex min-h-[38px] items-center text-[13.5px] text-tinte"
              data-testid="metarr-plan-projekt"
            >
              {gefuehrtesProjekt ? (
                <span>
                  <span className="font-medium">{gefuehrtesProjekt.kuerzel}</span>
                  {" — "}
                  {gefuehrtesProjekt.name}
                </span>
              ) : (
                <span className="text-tinte3">Kein Projekt gewählt</span>
              )}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="metarr-plan-anliegen">Anliegen</Label>
            <Textarea
              id="metarr-plan-anliegen"
              rows={3}
              maxLength={10_000}
              value={anliegen}
              onChange={(ereignis) => {
                setzeAnliegen(ereignis.target.value);
                vorschlagen.reset();
              }}
              placeholder="Zum Beispiel: Lege den nächsten Release-Meilenstein an und ordne die offenen Aufgaben sinnvoll zu."
              disabled={vorschlagen.isPending}
            />
          </div>
          <Button
            type="submit"
            className="md:mb-px"
            disabled={
              vorschlagen.isPending ||
              !ausgewaehltesProjekt ||
              !anliegen.trim()
            }
            isLoading={vorschlagen.isPending}
            loadingText="Wird vorbereitet"
          >
            Plan vorbereiten
          </Button>
        </div>
        {!projekte.isPending && !ich.isPending && !darfPlanen ? (
          <p className="mt-3 text-[12px] text-akzent" role="status">
            {aktiveProjekte.length === 0
              ? "Es gibt kein aktives Projekt, in dem du mindestens Mitglied und damit schreibberechtigt bist."
              : gefuehrtesProjekt
                ? `In ${gefuehrtesProjekt.kuerzel ?? gefuehrtesProjekt.name} kannst du nicht planen — dafür brauchst du mindestens die Rolle Mitglied. Wechsle das Projekt oben links.`
                : "Wähle oben links ein Projekt, für das ein Plan entstehen soll."}
          </p>
        ) : null}
        {vorschlagen.error ? (
          <div className="mt-3">
            <Fehlerzeile
              meldung={fehlerMeldung(vorschlagen.error)}
              onErneut={() => {
                if (ausgewaehltesProjekt && anliegen.trim()) {
                  vorschlagen.mutate({
                    projektId: ausgewaehltesProjekt,
                    satz: anliegen.trim(),
                  });
                }
              }}
            />
          </div>
        ) : null}
      </form>

      {laeufeOhnePlan.map((lauf) => (
        <div
          key={lauf.id}
          className="flex items-start gap-3 border-b border-linie py-3"
        >
          <span
            aria-hidden="true"
            className={cx(
              "mt-[7px] size-[5px] flex-none",
              lauf.zustand === "gescheitert" ? "bg-akzent" : "bg-tinte",
            )}
          />
          <div className="min-w-0 flex-1">
            <span className="block text-[13.5px] leading-[1.5] text-tinte">
              {lauf.satz}
            </span>
            <span
              className="mt-0.5 block font-mono text-[10.5px] text-tinte4"
              role="status"
            >
              {LAUFZUSTAENDE[lauf.zustand] ?? lauf.zustand} ·{" "}
              {vorZeit(lauf.angelegtAm)}
            </span>
            {lauf.rueckfrage ? (
              <ProjektplanRueckfrage
                lauf={lauf}
                kannAntworten={
                  lauf.projektId !== null &&
                  schreibbareProjektIds.has(lauf.projektId)
                }
              />
            ) : null}
            {lauf.letzterFehler ? (
              <span className="mt-1 block text-[12px] text-akzent">
                {lauf.letzterFehler}
              </span>
            ) : null}
          </div>
        </div>
      ))}

      {plaene.error || entwuerfe.error || projekte.error || ich.error ? (
        <div className="py-4">
          <Fehlerzeile
            meldung={fehlerMeldung(
              plaene.error ?? entwuerfe.error ?? projekte.error ?? ich.error,
            )}
            onErneut={() => {
              void Promise.all([
                plaene.refetch(),
                entwuerfe.refetch(),
                projekte.refetch(),
                ich.refetch(),
              ]);
            }}
          />
        </div>
      ) : offenePlaene.length === 0 && laeufeOhnePlan.length === 0 ? (
        <div className="border-b border-linie py-6">
          <Mikrolabel grad={11.5}>
            Kein Projektplan wartet auf eine menschliche Prüfung.
          </Mikrolabel>
        </div>
      ) : (
        offenePlaene.map((plan) => {
          const lauf = laufZu.get(plan.entwurfId);
          const istOffen = aufgeklappt === plan.id;
          return (
            <div key={plan.id} className="border-b border-linie py-[15px]">
              <div className="flex items-start gap-3.5">
                <span className="min-w-0 flex-1">
                  <Mikrolabel grad={11} className="text-akzent">
                    {PLANZUSTAENDE[plan.zustand] ?? plan.zustand}
                  </Mikrolabel>
                  <p className="mt-1 text-[14px] leading-[1.5] text-tinte">
                    {plan.titel}
                  </p>
                  <p className="mt-1.5 max-w-[68ch] text-[13px] font-light leading-[1.55] text-tinte3">
                    {plan.zusammenfassung}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                    <Mikrolabel grad={10.5}>
                      Lauf: {lauf ? LAUFZUSTAENDE[lauf.zustand] ?? lauf.zustand : "abgeschlossen"}
                    </Mikrolabel>
                    <Mikrolabel grad={10.5}>{vorZeit(plan.angelegtAm)}</Mikrolabel>
                  </div>
                </span>
                <button
                  type="button"
                  onClick={() => setzeAufgeklappt(istOffen ? null : plan.id)}
                  aria-expanded={istOffen}
                  className={cx(
                    "mt-0.5 flex-none cursor-pointer font-mono text-[11.5px] text-akzent hover:underline",
                    focusRing,
                  )}
                >
                  {istOffen ? "Schließen" : "Plan prüfen"}
                </button>
              </div>
              {istOffen ? (
                <ProjektplanDetails
                  plan={plan}
                  kannSchreiben={schreibbareProjektIds.has(plan.projektId)}
                />
              ) : null}
            </div>
          );
        })
      )}
    </section>
  );
}
